import { HttpClient } from '@opallabs/api'

export type ConnectionStatus =
  | 'checking'
  | 'connected'
  | 'disconnected'
  | 'unavailable'
  | 'error'

export type Connection = Readonly<{
  status: ConnectionStatus
  /** Display names of the provider accounts the user's connection can reach. */
  accounts: readonly string[]
}>

type AccountsResponse = {
  data?: Array<{ display_name?: string }>
}

// 412: the user hasn't connected this provider. 404: the provider isn't
// provisioned for this Opal at all, so there's nothing to connect to.
const statusForHttpError = (httpStatus?: number): ConnectionStatus => {
  if (httpStatus === 412) return 'disconnected'
  if (httpStatus === 404) return 'unavailable'
  return 'error'
}

// The monolith proxies a per-user "list my accounts" call for each provider.
// It answers 412 when the user hasn't connected that provider, so the same
// request tells us both whether a connection exists and that its token still
// works — a stale or revoked token surfaces as an error, not as "connected".
export const fetchConnection = async (
  statusPath: string
): Promise<Connection> => {
  try {
    const response = await HttpClient().get<AccountsResponse>(statusPath)
    const accounts = (response.data.data ?? [])
      .map((account) => account.display_name)
      .filter((name): name is string => Boolean(name))
    return { status: 'connected', accounts }
  } catch (error) {
    const httpStatus = (error as { response?: { status?: number } })?.response
      ?.status
    return { status: statusForHttpError(httpStatus), accounts: [] }
  }
}
